import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Loader2, SlidersHorizontal } from "lucide-react";

export function CustomFieldsCard({ entityType, entityId }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!entityId) return;
    const load = async () => {
      try {
        const [fieldsRes, valuesRes] = await Promise.all([
          axios.get(`${API}/custom-fields?entity_type=${entityType}`),
          axios.get(`${API}/custom-fields/values/${entityType}/${entityId}`)
        ]);
        setFields(fieldsRes.data || []);
        setValues(valuesRes.data || {});
      } catch (err) {
        // Error handled silently — non-critical operation
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const updateValue = (fieldId, val) => {
    setValues({ ...values, [fieldId]: val });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/custom-fields/values/${entityType}/${entityId}`, values);
      setDirty(false);
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setSaving(false);
    }
  };

  if (fields.length === 0) return null;

  return (
    <Card data-testid="custom-fields-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4" />
          Custom Fields
        </CardTitle>
        {dirty && (
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-custom-fields">
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Save
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {fields.map((field) => (
          <CustomFieldInput
            key={field.field_id}
            field={field}
            value={values[field.field_id]}
            onChange={(val) => updateValue(field.field_id, val)}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function CustomFieldInput({ field, value, onChange }) {
  const { field_name, field_type, options, required } = field;

  switch (field_type) {
    case "text":
      return (
        <div>
          <Label className="text-sm">{field_name}{required && <span className="text-red-500 ml-1">*</span>}</Label>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field_name} data-testid={`cf-${field.field_key}`} />
        </div>
      );
    case "number":
      return (
        <div>
          <Label className="text-sm">{field_name}{required && <span className="text-red-500 ml-1">*</span>}</Label>
          <Input type="number" value={value || ""} onChange={(e) => onChange(parseFloat(e.target.value) || "")} placeholder="0" data-testid={`cf-${field.field_key}`} />
        </div>
      );
    case "date":
      return (
        <div>
          <Label className="text-sm">{field_name}{required && <span className="text-red-500 ml-1">*</span>}</Label>
          <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`cf-${field.field_key}`} />
        </div>
      );
    case "dropdown":
      return (
        <div>
          <Label className="text-sm">{field_name}{required && <span className="text-red-500 ml-1">*</span>}</Label>
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger data-testid={`cf-${field.field_key}`}><SelectValue placeholder={`Select ${field_name}`} /></SelectTrigger>
            <SelectContent>
              {(options || []).map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    case "checkbox":
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 rounded text-indigo-600"
            data-testid={`cf-${field.field_key}`}
          />
          <Label className="text-sm cursor-pointer">{field_name}</Label>
        </div>
      );
    case "multi_select":
      const selected = Array.isArray(value) ? value : [];
      return (
        <div>
          <Label className="text-sm">{field_name}{required && <span className="text-red-500 ml-1">*</span>}</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {(options || []).map(opt => {
              const isSelected = selected.includes(opt);
              return (
                <Badge
                  key={opt}
                  variant={isSelected ? "default" : "outline"}
                  className={`cursor-pointer text-xs ${isSelected ? "bg-indigo-600" : "hover:bg-slate-100"}`}
                  onClick={() => {
                    if (isSelected) onChange(selected.filter(s => s !== opt));
                    else onChange([...selected, opt]);
                  }}
                  data-testid={`cf-${field.field_key}-${opt}`}
                >
                  {opt}
                </Badge>
              );
            })}
          </div>
        </div>
      );
    case "file":
      return (
        <div>
          <Label className="text-sm">{field_name}</Label>
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="File URL" data-testid={`cf-${field.field_key}`} />
        </div>
      );
    default:
      return null;
  }
}
